import { getLastDayFromPersonalPickupString } from 'helpers/getLastDayFromPersonalPickupString';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';
import {
    AvailabilityStatusEnum,
    AvailabilityType,
    StoreAvailabilityType,
    StoreListAvailabilityStatusEnum,
} from 'types/availability';

export type VirtualSetProductAvailability = {
    availability: AvailabilityType;
    defaultStoreAvailability: StoreAvailabilityType | null;
    deliveryDaysFromCentralStore: string | null;
};

export type VirtualSetCalculatedAvailability = {
    centralStore: {
        isInStock: boolean;
        message: string;
    };
    defaultStore: {
        isInStock: boolean;
        message: string;
        storeName: string | null;
    } | null;
};

export const useVirtualSetCalculatedAvailability = (
    productAvailabilities: VirtualSetProductAvailability[],
): VirtualSetCalculatedAvailability => {
    const { t } = useTranslation();

    return useMemo(() => {
        if (productAvailabilities.length === 0) {
            return {
                centralStore: {
                    isInStock: false,
                    message: t('Unavailable in eshop'),
                },
                defaultStore: null,
            };
        }

        const centralInStock = productAvailabilities.every(
            (product) => product.availability.status === AvailabilityStatusEnum.inStock,
        );

        const centralStore = {
            isInStock: centralInStock,
            message: centralInStock ? t('Available in eshop') : t('Unavailable in eshop'),
        };

        const defaultStoreAvailabilities = productAvailabilities.map((product) => product.defaultStoreAvailability);

        if (defaultStoreAvailabilities.some((storeAvailability) => storeAvailability === null)) {
            return {
                centralStore,
                defaultStore: null,
            };
        }

        const storeName = defaultStoreAvailabilities[0]?.storeName ?? null;

        const allInStock = defaultStoreAvailabilities.every(
            (storeAvailability) => storeAvailability!.availabilityStatus === StoreListAvailabilityStatusEnum.inStock,
        );

        let maxDaysCount = 0;
        let maxDays: string | null = null;

        productAvailabilities.forEach((product) => {
            if (!product.deliveryDaysFromCentralStore) {
                return;
            }

            const { days, daysCount } = getLastDayFromPersonalPickupString(product.deliveryDaysFromCentralStore);

            if (daysCount > maxDaysCount) {
                maxDaysCount = daysCount;
                maxDays = days;
            }
        });

        let message: string;

        if (allInStock) {
            message = `${t('Available')} ${t('in store {{ store }}', { store: storeName })}`;
        } else if (maxDaysCount > 0) {
            message =
                t('Available in {{ days }} days', {
                    days: maxDays,
                    count: maxDaysCount,
                }) +
                ' ' +
                t('in store {{ store }}', { store: storeName });
        } else {
            message = `${t('Unavailable')} ${t('in store {{ store }}', { store: storeName })}`;
        }

        return {
            centralStore,
            defaultStore: {
                isInStock: allInStock || maxDaysCount > 0,
                message,
                storeName,
            },
        };
    }, [productAvailabilities, t]);
};
